import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    PaymentElement,
    Elements,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { toast } from 'react-hot-toast';
import api from '../lib/api';

// Replace with your publishable key
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx');

interface PaymentFormProps {
    amount: number;
    onSuccess: (paymentIntentId: string) => void;
    onCancel: () => void;
    purpose?: 'order_payment' | 'wallet_topup';
}

const CheckoutForm: React.FC<{ onSuccess: (id: string) => void; onCancel: () => void }> = ({ onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            const result = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: window.location.origin + '/payment-success', // You'll need to handle this route
                },
                redirect: 'if_required',
            });

            if (result.error) {
                setError(result.error.message || 'Payment failed');
                toast.error(result.error.message || 'Payment failed');
            } else if (result.paymentIntent) {
                if (result.paymentIntent.status === 'succeeded' || result.paymentIntent.status === 'requires_capture') {
                    toast.success('Payment authorized successfully!');
                    onSuccess(result.paymentIntent.id);
                }
            }
        } catch (err: any) {
            setError(err.message);
            toast.error(err.message);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white p-4 rounded-xl border border-gray-200">
                <PaymentElement />
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}

            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                    disabled={processing}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={!stripe || processing}
                    className="flex-1 py-3 px-4 bg-[#10b981] text-white rounded-xl font-bold hover:bg-[#059669] transition-colors shadow-lg shadow-[#10b981]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    {processing ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                        `Pay Now`
                    )}
                </button>
            </div>
        </form>
    );
};



const PaymentForm: React.FC<PaymentFormProps> = ({ amount, onSuccess, onCancel, purpose = 'order_payment' }) => {
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setClientSecret(null);
        const createIntent = async () => {
            try {
                const { data } = await api.post('/payments/create-intent', {
                    amount: amount,
                    currency: 'inr',
                    metadata: { integration_check: 'accept_a_payment' },
                    purpose,
                    paymentMethodTypes: ['card'],
                });

                setClientSecret(data.clientSecret);
            } catch (err: any) {
                setError(err.response?.data?.message || err.message);
                toast.error('Failed to load payment options');
            }
        };

        createIntent();
    }, [amount, purpose]);

    if (error) {
        return <div className="text-red-500 text-center p-4">Error: {error}</div>;
    }

    if (!clientSecret) {
        return <div className="flex justify-center p-4"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;
    }

    return (
        <Elements stripe={stripePromise} options={{ clientSecret }} key={clientSecret}>
            <CheckoutForm onSuccess={onSuccess} onCancel={onCancel} />
        </Elements>
    );
};

export default PaymentForm;
